import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { cloneDeep } from 'lodash'
import Papa from 'papaparse'
import { Modal, Tabs, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline'

import {
  buildShippingMethodPrice,
  findOrCreateBillingService,
  getCurrency,
  getShippingLabelCategories,
  isShippingMethodPrice,
  removeShippingMethodPrices,
  shippingMethodIdsIn,
  updateDefaultPriceConfiguration,
} from '../utils'
import { SERVICE_TYPES } from '../constants'
import { buildShippingPriceExport, parseShippingPriceRows } from '../shippingPriceCsv'
import { CSV_FORMATS } from '../../../utils/csvUtils'
import { fetchBillingShippingMethods } from '../../../services'
import RangeBillingPriceConfigTable from '../RangeBillingPriceConfigTable'
import { UNITS_IDS } from '../RangeBillingPriceConfigTable/constants'

const DEFAULT_TAB = 'default'

const toTableData = priceConfiguration => {
  return priceConfiguration.map(priceRange => ({
    from: { value: priceRange.from, isDisabled: true, scale: 2 },
    to: { value: priceRange.to, scale: 2 },
    price: { value: priceRange.price },
    price_mode: { value: priceRange.price_mode || UNITS_IDS.TOTAL },
    excess: { value: priceRange.excess },
    range_exclusion: { value: priceRange.range_exclusion },
  }))
}

const fromTableData = tableData => {
  return tableData.map(row => ({
    from: row.from.value,
    to: row.to.value,
    price: row.price.value,
    price_mode: row.price_mode.value,
    excess: row.excess.value,
    range_exclusion: row.range_exclusion.value,
  }))
}

const ZONE_PRICE = SERVICE_TYPES.SERVICE_SHIPPING_LABEL
const EXTENDED_ZONE = SERVICE_TYPES.SERVICE_SHIPPING_LABEL_EXTENDED_ZONE

const tableKey = (shippingMethodId, zone, serviceType = ZONE_PRICE) => `${shippingMethodId}-${zone}-${serviceType}`

// The extended zone surcharge is one flat price per label: no weight ranges to edit.
const toExtendedZoneTableData = priceConfiguration => {
  return priceConfiguration.map(priceRange => ({
    from: { value: priceRange.from, isDisabled: true, scale: 2 },
    to: { value: priceRange.to, isDisabled: true, scale: 2 },
    price: { value: priceRange.price },
    price_mode: { value: priceRange.price_mode || UNITS_IDS.TOTAL, isDisabled: true },
    excess: { value: priceRange.excess },
    range_exclusion: { value: priceRange.range_exclusion },
  }))
}

const StatusIcon = ({ isValid }) => {
  return isValid ? <CheckCircleIcon className="inline h-4 w-4 text-green-500" /> : <ExclamationCircleIcon className="inline h-4 w-4 text-yellow-500" />
}

// Tabs over the national shipping prices: the mandatory default prices first, then one optional tab
// per shipping method. A zone a shipping method has no price for bills the default price, so a
// shipping method tab starts empty and ops add only the zones that were negotiated differently.
const ShippingMethodPrices = ({ storeBillingServices, onUpdateStoreBillingServices, onIsValid, storeId, warehouse, defaultTab, isDefaultValid }) => {
  const { i18n } = useTranslation()
  const countryZones = Object.values(getShippingLabelCategories(warehouse?.country))
  const hasZones = countryZones.length > 0
  const currency = getCurrency(warehouse?.country)

  const pricedShippingMethodIds = shippingMethodIdsIn(storeBillingServices)
  const pricedRows = storeBillingServices.filter(isShippingMethodPrice)

  const [openShippingMethodIds, setOpenShippingMethodIds] = useState(pricedShippingMethodIds)
  const [activeKey, setActiveKey] = useState(DEFAULT_TAB)
  const [tablesValidity, setTablesValidity] = useState({})
  const [isAdding, setIsAdding] = useState(false)
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState('')
  const [removingShippingMethodId, setRemovingShippingMethodId] = useState(null)
  // Price tables read their rows on mount only, so an import remounts the tables of its tab.
  const [importVersions, setImportVersions] = useState({})
  const [importSummary, setImportSummary] = useState(null)

  const { data: offeredShippingMethods = [], isLoading, isError } = useQuery(
    ['store_billing_shipping_methods', warehouse?.id],
    () => fetchBillingShippingMethods({ warehouse_id: warehouse?.id }),
    { enabled: hasZones && Boolean(warehouse?.id), refetchOnWindowFocus: false, retry: false }
  )

  // Services load after mount (and change on duplicate), so priced methods get their tab then.
  const pricedIdsKey = pricedShippingMethodIds.join(',')
  useEffect(() => {
    setOpenShippingMethodIds(current => [...current, ...pricedShippingMethodIds.filter(id => !current.includes(id))])
  }, [pricedIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // A price for a zone the method stopped covering is rejected on save, so it keeps the section
  // invalid until it is removed.
  const hasUncoveredPrices = pricedRows.some(row => {
    const offered = offeredShippingMethods.find(({ id }) => id === row.shipping_method_id)
    return offered && !offered.categories.includes(row.service_type_category)
  })

  useEffect(() => {
    onIsValid(Object.values(tablesValidity).every(Boolean) && !hasUncoveredPrices)
  }, [tablesValidity, hasUncoveredPrices]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasZones) {
    return defaultTab
  }

  const findOffered = shippingMethodId => offeredShippingMethods.find(({ id }) => id === shippingMethodId)

  const shippingMethodName = shippingMethodId => {
    const offered = findOffered(shippingMethodId)
    if (offered) return `${offered.shipping_name} (${offered.carrier_name})`

    const pricedRow = pricedRows.find(row => row.shipping_method_id === shippingMethodId)
    return pricedRow?.shipping_method_name || `#${shippingMethodId}`
  }

  const pricedZones = shippingMethodId => {
    return pricedRows.filter(row => row.shipping_method_id === shippingMethodId).map(row => row.service_type_category)
  }

  const coversZone = (shippingMethodId, zone) => {
    const offered = findOffered(shippingMethodId)
    return !offered || offered.categories.includes(zone)
  }

  // Zones the method covers plus zones that already have a price: a price for a zone the method
  // stopped covering is rejected on save, so it has to stay visible to be removed.
  const shownZones = shippingMethodId => {
    const zones = [...(findOffered(shippingMethodId)?.categories || []), ...pricedZones(shippingMethodId)]
    return countryZones.filter(zone => zones.includes(zone))
  }

  const findPrice = (shippingMethodId, zone, serviceType = ZONE_PRICE) => {
    return pricedRows.find(row => row.shipping_method_id === shippingMethodId && row.service_type_category === zone && row.service_type === serviceType)
  }

  const isShippingMethodValid = shippingMethodId => {
    const areTablesValid = Object.entries(tablesValidity).every(([key, isValid]) => !key.startsWith(`${shippingMethodId}-`) || isValid)
    return areTablesValid && pricedZones(shippingMethodId).every(zone => coversZone(shippingMethodId, zone))
  }

  const forgetTables = keys => {
    setTablesValidity(current => {
      const next = { ...current }
      keys.forEach(key => delete next[key])
      return next
    })
  }

  const handleAdd = () => {
    const shippingMethodId = Number(selectedShippingMethodId)
    if (!shippingMethodId) return

    setOpenShippingMethodIds(current => [...current, shippingMethodId])
    setActiveKey(String(shippingMethodId))
    setSelectedShippingMethodId('')
    setIsAdding(false)
  }

  const handleRemove = () => {
    const shippingMethodId = removingShippingMethodId
    forgetTables(pricedZones(shippingMethodId).flatMap(zone => [tableKey(shippingMethodId, zone), tableKey(shippingMethodId, zone, EXTENDED_ZONE)]))
    setOpenShippingMethodIds(current => current.filter(id => id !== shippingMethodId))
    setActiveKey(DEFAULT_TAB)
    setRemovingShippingMethodId(null)
    if (pricedShippingMethodIds.includes(shippingMethodId)) {
      onUpdateStoreBillingServices(removeShippingMethodPrices(storeBillingServices, shippingMethodId))
    }
  }

  const handleEdit = (targetKey, action) => {
    if (action === 'add') setIsAdding(true)
    if (action === 'remove') setRemovingShippingMethodId(Number(targetKey))
  }

  const handleUseDefault = (shippingMethodId, zone, serviceType) => {
    forgetTables([tableKey(shippingMethodId, zone, serviceType)])
    onUpdateStoreBillingServices(removeShippingMethodPrices(storeBillingServices, shippingMethodId, zone, serviceType))
  }

  const handleSetPrice = (shippingMethodId, zone, serviceType) => {
    const price = buildShippingMethodPrice({ billingServices: storeBillingServices, shippingMethodId, category: zone, currency, storeId, warehouseId: warehouse?.id, serviceType })
    onUpdateStoreBillingServices([...cloneDeep(storeBillingServices), price])
  }

  const handleUpdateTable = (shippingMethodId, zone, serviceType, tableData) => {
    const updated = cloneDeep(storeBillingServices).map(row => {
      if (isShippingMethodPrice(row) && row.shipping_method_id === shippingMethodId && row.service_type_category === zone && row.service_type === serviceType) {
        row.price_configuration = fromTableData(tableData)
      }
      return row
    })
    onUpdateStoreBillingServices(updated)
  }

  const handleTableValidity = (key, isValid) => {
    setTablesValidity(current => (current[key] === isValid ? current : { ...current, [key]: isValid }))
  }

  const tabZones = tabKey => (tabKey === DEFAULT_TAB ? countryZones : shownZones(Number(tabKey)))

  const importIntoDefault = (prices, serviceType = SERVICE_TYPES.SERVICE_SHIPPING_LABEL, baseServices = storeBillingServices) => {
    return Object.entries(prices).reduce((services, [zone, priceConfiguration]) => {
      const defaultService = findOrCreateBillingService({
        serviceType,
        serviceTypeCategory: zone,
        billingServices: services,
        currency,
        storeId,
        warehouseId: warehouse?.id,
      })
      return updateDefaultPriceConfiguration(services, defaultService, priceConfiguration)
    }, baseServices)
  }

  const extendedZoneConfiguration = price => [{ from: 0, to: null, price, price_mode: 'TOTAL', excess: false, range_exclusion: null }]

  const importIntoShippingMethod = (shippingMethodId, prices, serviceType = ZONE_PRICE, baseServices = cloneDeep(storeBillingServices)) => {
    return Object.entries(prices).reduce((services, [zone, priceConfiguration]) => {
      const existing = services.find(
        row => isShippingMethodPrice(row) && row.shipping_method_id === shippingMethodId && row.service_type_category === zone && row.service_type === serviceType
      )
      if (existing) {
        return services.map(row => (row === existing ? { ...row, price_configuration: priceConfiguration } : row))
      }
      const price = buildShippingMethodPrice({ billingServices: services, shippingMethodId, category: zone, currency, storeId, warehouseId: warehouse?.id, serviceType })
      return [...services, { ...price, price_configuration: priceConfiguration }]
    }, baseServices)
  }

  const applyImport = (tabKey, rows) => {
    const { error, row, prices, ignoredColumns, invalidZones, invalidExtendedZones, extendedZonePrices } = parseShippingPriceRows(rows, countryZones, warehouse?.country)
    if (error) {
      setImportSummary({ error, row })
      return
    }

    const allowedZones = tabZones(tabKey)
    const importable = Object.fromEntries(Object.entries(prices).filter(([zone]) => allowedZones.includes(zone)))
    const notCovered = Object.keys(prices).filter(zone => !allowedZones.includes(zone))

    const isDefaultTab = tabKey === DEFAULT_TAB
    const extendedZones = Object.fromEntries(Object.entries(extendedZonePrices).filter(([zone]) => allowedZones.includes(zone)))

    if (Object.keys(importable).length > 0 || Object.keys(extendedZones).length > 0) {
      const extendedPrices = Object.fromEntries(Object.entries(extendedZones).map(([zone, price]) => [zone, extendedZoneConfiguration(price)]))
      const updated = isDefaultTab
        ? importIntoDefault(extendedPrices, EXTENDED_ZONE, importIntoDefault(importable))
        : importIntoShippingMethod(Number(tabKey), extendedPrices, EXTENDED_ZONE, importIntoShippingMethod(Number(tabKey), importable))
      onUpdateStoreBillingServices(updated)
      setImportVersions(current => ({ ...current, [tabKey]: (current[tabKey] || 0) + 1 }))
    }
    setImportSummary({
      filled: Object.keys(importable).length,
      extendedFilled: Object.keys(extendedZones).length,
      ignoredColumns,
      invalidZones,
      invalidExtendedZones,
      notCovered,
      isShippingMethod: !isDefaultTab,
    })
  }

  const handleImportFile = (tabKey, event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    Papa.parse(file, { skipEmptyLines: true, complete: ({ data }) => applyImport(tabKey, data) })
  }

  const defaultPriceConfiguration = zone => {
    return storeBillingServices.find(row => row.service_type === SERVICE_TYPES.SERVICE_SHIPPING_LABEL && row.service_type_category === zone && !row.shipping_method_id)
      ?.price_configuration
  }

  const defaultExtendedZonePrice = zone => {
    const row = storeBillingServices.find(service => service.service_type === SERVICE_TYPES.SERVICE_SHIPPING_LABEL_EXTENDED_ZONE && service.service_type_category === zone && !service.shipping_method_id)
    return row?.price_configuration?.[0]?.price ?? null
  }

  // Each tab exports only what it shows: a zone without a price of its own stays blank.
  const exportedPriceConfiguration = (tabKey, zone) => {
    if (tabKey === DEFAULT_TAB) return defaultPriceConfiguration(zone)
    return findPrice(Number(tabKey), zone)?.price_configuration
  }

  const exportedExtendedZonePrice = (tabKey, zone) => {
    if (tabKey === DEFAULT_TAB) return defaultExtendedZonePrice(zone)
    return findPrice(Number(tabKey), zone, EXTENDED_ZONE)?.price_configuration?.[0]?.price ?? null
  }

  const handleDownloadTemplate = tabKey => {
    const csv = buildShippingPriceExport({
      zones: tabZones(tabKey),
      country: warehouse?.country,
      excessLabel: i18n.t('billing.shipping_method_prices.excess_row'),
      priceConfigurationFor: zone => exportedPriceConfiguration(tabKey, zone),
      extendedZoneLabel: i18n.t('billing.shipping_method_prices.extended_zone_row'),
      extendedZonePriceFor: zone => exportedExtendedZonePrice(tabKey, zone),
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    link.download = 'precos_envio.csv'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const zoneTitle = zone => i18n.t(`billing.national_shipping_label_services.titles.${zone}`)
  // Each tab imports into itself, so it is always clear which prices a file replaces. A country
  // without a declared number format gets no CSV tools rather than one read in another's format.
  const renderImportToolbar = tabKey => CSV_FORMATS[warehouse?.country] && (
    <div className="mb-4 flex items-center justify-end gap-4 text-sm">
      <button type="button" className="text-blue-500 hover:underline" onClick={() => handleDownloadTemplate(tabKey)}>
        {i18n.t('billing.shipping_method_prices.download_template')}
      </button>
      <label className="cursor-pointer text-blue-500 hover:underline">
        {i18n.t('billing.shipping_method_prices.import_csv')}
        <input type="file" accept=".csv,text/csv" className="hidden" data-testid="shipping-price-csv-input" onChange={event => handleImportFile(tabKey, event)} />
      </label>
    </div>
  )

  const zoneList = zones => zones.map(zone => zoneTitle(zone)).join(', ')

  const tableHeaders = [
    { label: i18n.t('billing.table_headers.from_weight'), id: 'from' },
    { label: i18n.t('billing.table_headers.to_weight'), id: 'to' },
    { label: i18n.t('billing.table_headers.price'), id: 'price' },
    { label: i18n.t('billing.table_headers.unit'), id: 'unit' },
    { label: null, id: 'actions' },
  ]

  const unitOptionsLabels = {
    TOTAL: i18n.t('billing.national_shipping_label_services.unit_options.TOTAL'),
    EXCESS_PER_UNIT: i18n.t('billing.national_shipping_label_services.unit_options.EXCESS_PER_UNIT'),
  }


  const availableShippingMethods = offeredShippingMethods.filter(({ id, categories }) => {
    return !openShippingMethodIds.includes(id) && categories.some(zone => countryZones.includes(zone))
  })

  // A zone price or extended zone surcharge of a shipping method: its table when it has one, or a
  // placeholder saying the default applies. Either way it keeps its place, so defining a price opens
  // the table where the placeholder was.
  const renderPrice = (shippingMethodId, zone, serviceType) => {
    const price = findPrice(shippingMethodId, zone, serviceType)
    const key = tableKey(shippingMethodId, zone, serviceType)
    const isExtendedZone = serviceType === EXTENDED_ZONE

    if (!price) {
      return (
        <div className="flex items-center justify-between rounded border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
          <span>{i18n.t('billing.shipping_method_prices.uses_default')}</span>
          <button
            type="button"
            className="text-blue-500 hover:underline"
            data-testid={`set-price-${zone}-${serviceType}`}
            onClick={() => handleSetPrice(shippingMethodId, zone, serviceType)}
          >
            {i18n.t('billing.shipping_method_prices.set_price')}
          </button>
        </div>
      )
    }

    return (
      <>
        <div className="flex items-center justify-end gap-2">
          <StatusIcon isValid={tablesValidity[key] !== false} />
          <button
            type="button"
            className="text-sm text-blue-500 hover:underline"
            data-testid={`use-default-${zone}-${serviceType}`}
            onClick={() => handleUseDefault(shippingMethodId, zone, serviceType)}
          >
            {i18n.t('billing.shipping_method_prices.use_default')}
          </button>
        </div>
        <RangeBillingPriceConfigTable
          key={`${key}-${importVersions[String(shippingMethodId)] || 0}`}
          headers={tableHeaders}
          priceConfiguration={isExtendedZone ? toExtendedZoneTableData(price.price_configuration) : toTableData(price.price_configuration)}
          currency={currency}
          onUpdatePriceConfiguration={tableData => handleUpdateTable(shippingMethodId, zone, serviceType, tableData)}
          onIsValid={isValid => handleTableValidity(key, isValid)}
          availableUnitOptions={isExtendedZone ? [UNITS_IDS.TOTAL] : [UNITS_IDS.TOTAL, UNITS_IDS.EXCESS_PER_UNIT]}
          unitOptionsLabels={unitOptionsLabels}
        />
      </>
    )
  }

  // Every zone keeps its place in the fixed zone order: its price, then its extended zone surcharge.
  const renderShippingMethodTab = shippingMethodId => {
    return (
      <div className="flex flex-col gap-6">
        <div>
          {renderImportToolbar(String(shippingMethodId))}
          <p className="rounded bg-blue-50 p-3 text-sm text-gray-600">{i18n.t('billing.shipping_method_prices.not_added_notice')}</p>
        </div>

        {shownZones(shippingMethodId).map(zone => (
          <div key={tableKey(shippingMethodId, zone)} className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <h2 className={`text-xl ${findPrice(shippingMethodId, zone) ? 'text-gray-500' : 'text-gray-400'}`}>{zoneTitle(zone)}</h2>
              {!coversZone(shippingMethodId, zone) && <span className="text-sm text-red-500">({i18n.t('billing.shipping_method_prices.no_longer_covered')})</span>}
            </div>
            {renderPrice(shippingMethodId, zone, ZONE_PRICE)}

            <h2 className={`mt-2 text-xl ${findPrice(shippingMethodId, zone, EXTENDED_ZONE) ? 'text-gray-500' : 'text-gray-400'}`}>
              {i18n.t(`billing.national_shipping_label_services.extended_zone_titles.${zone}`)}
            </h2>
            {renderPrice(shippingMethodId, zone, EXTENDED_ZONE)}
          </div>
        ))}
      </div>
    )
  }

  // Every tab stays rendered, so the price tables of a hidden tab keep reporting their validity.
  const items = [
    {
      key: DEFAULT_TAB,
      label: (
        <span className="flex items-center gap-1">
          {i18n.t('billing.shipping_method_prices.default_tab')} <StatusIcon isValid={isDefaultValid} />
        </span>
      ),
      closable: false,
      forceRender: true,
      children: (
        <>
          {renderImportToolbar(DEFAULT_TAB)}
          <div key={`default-${importVersions[DEFAULT_TAB] || 0}`}>{defaultTab}</div>
        </>
      ),
    },
    ...openShippingMethodIds.map(shippingMethodId => ({
      key: String(shippingMethodId),
      label: (
        <span className="flex items-center gap-1">
          {shippingMethodName(shippingMethodId)} <StatusIcon isValid={isShippingMethodValid(shippingMethodId)} />
        </span>
      ),
      forceRender: true,
      children: renderShippingMethodTab(shippingMethodId),
    })),
  ]

  return (
    <>
      <Tabs
        type="editable-card"
        activeKey={activeKey}
        onChange={setActiveKey}
        onEdit={handleEdit}
        items={items}
        addIcon={
          <Tooltip title={i18n.t('billing.shipping_method_prices.add')}>
            <PlusOutlined />
          </Tooltip>
        }
      />

      <Modal
        open={isAdding}
        title={i18n.t('billing.shipping_method_prices.add')}
        okText={i18n.t('billing.shipping_method_prices.add')}
        cancelText={i18n.t('billing.shipping_method_prices.cancel')}
        okButtonProps={{ disabled: !selectedShippingMethodId }}
        onOk={handleAdd}
        onCancel={() => setIsAdding(false)}
      >
        {isError && <p className="mb-2 text-sm italic text-red-500">{i18n.t('billing.shipping_method_prices.load_error')}</p>}
        <select
          className="w-full rounded border border-gray-300 text-sm"
          value={selectedShippingMethodId}
          onChange={event => setSelectedShippingMethodId(event.target.value)}
          disabled={isLoading || availableShippingMethods.length === 0}
        >
          <option value="" hidden>
            {isLoading ? i18n.t('billing.shipping_method_prices.loading') : i18n.t('billing.shipping_method_prices.select_shipping_method')}
          </option>
          {availableShippingMethods.map(({ id, shipping_name, carrier_name }) => (
            <option key={id} value={id}>
              {shipping_name} ({carrier_name})
            </option>
          ))}
        </select>
      </Modal>

      <Modal
        open={importSummary !== null}
        title={i18n.t('billing.shipping_method_prices.import_csv')}
        onCancel={() => setImportSummary(null)}
        footer={null}
      >
        {importSummary?.error ? (
          <p className="text-red-500">{i18n.t(`billing.shipping_method_prices.import_error_${importSummary.error}`, { row: importSummary.row })}</p>
        ) : (
          <div className="flex flex-col gap-2 text-sm">
            <p>{i18n.t('billing.shipping_method_prices.import_filled', { count: importSummary?.filled })}</p>
            {importSummary?.extendedFilled > 0 && <p>{i18n.t('billing.shipping_method_prices.import_extended_zone_filled', { count: importSummary.extendedFilled })}</p>}
            {importSummary?.isShippingMethod && <p className="text-gray-500">{i18n.t('billing.shipping_method_prices.import_shipping_method_note')}</p>}
            {importSummary?.notCovered?.length > 0 && (
              <p className="text-yellow-600">{i18n.t('billing.shipping_method_prices.import_not_covered', { zones: zoneList(importSummary.notCovered) })}</p>
            )}
            {importSummary?.invalidZones?.length > 0 && (
              <p className="text-yellow-600">{i18n.t('billing.shipping_method_prices.import_invalid', { zones: zoneList(importSummary.invalidZones) })}</p>
            )}
            {importSummary?.invalidExtendedZones?.length > 0 && (
              <p className="text-yellow-600">{i18n.t('billing.shipping_method_prices.import_invalid_extended_zone', { zones: zoneList(importSummary.invalidExtendedZones) })}</p>
            )}
            {importSummary?.ignoredColumns?.length > 0 && (
              <p className="text-gray-500">{i18n.t('billing.shipping_method_prices.import_ignored_columns', { columns: importSummary.ignoredColumns.join(', ') })}</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={removingShippingMethodId !== null}
        title={removingShippingMethodId !== null ? shippingMethodName(removingShippingMethodId) : ''}
        okText={i18n.t('billing.shipping_method_prices.remove')}
        okButtonProps={{ danger: true }}
        cancelText={i18n.t('billing.shipping_method_prices.cancel')}
        onOk={handleRemove}
        onCancel={() => setRemovingShippingMethodId(null)}
      >
        {i18n.t('billing.shipping_method_prices.remove_confirm')}
      </Modal>
    </>
  )
}

export default ShippingMethodPrices
